import { ShieldCheck, AlertTriangle, Key, AlertCircle } from 'lucide-react'
import { useAuthedFetch, useAdminUsers } from '../../hooks/useAuthedFetch'
import { useTimedOut } from '../../hooks/useTimedOut'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import PageHeader from '../../components/ui/PageHeader'
import EmptyState from '../../components/ui/EmptyState'
import { humaniseAction, userLabel } from './adminFormat'

interface SecurityEvent {
  event_id: string
  timestamp: string
  user_id: string
  action_type?: string
  resource_id?: string
}

export default function SecurityReports() {
  // Lambda returns { security_events: [...], count }. Direct-fetch path (see
  // hooks/useAuthedFetch) because the axios read hangs inside /admin.
  const { data, isLoading, isError, refetch } =
    useAuthedFetch<{ security_events: SecurityEvent[]; count: number }>('/admin/security')
  // UX-046: events carry a user id; the directory turns it into an email. If the
  // directory itself fails the id is shown as is, which is still true.
  const { data: directory } = useAdminUsers()
  const emailsById = new Map((directory?.users ?? []).map((u) => [u.user_id, u.email]))

  const loadTimedOut = useTimedOut(isLoading)
  if (isLoading && !loadTimedOut) return <LoadingSkeleton rows={4} />

  // Split security events into permission changes and failed access
  const permissionChanges = data?.security_events?.filter(
    (e) => e.action_type?.includes('permission') || e.action_type?.includes('share')
  ) || []
  const failedAccess = data?.security_events?.filter(
    (e) => e.action_type?.includes('denied') || e.action_type?.includes('forbidden') || e.action_type?.includes('blocked')
  ) || []

  // UX-046: a failed load is not an all-clear. With no data the sections say so
  // instead of "No recent permission changes".
  const loadFailed = isError || (loadTimedOut && !data)
  const unavailable = (
    <EmptyState
      compact
      icon={AlertCircle}
      title={'\u2014'}
      description="Unavailable — the security events did not load. Use Retry above."
    />
  )

  return (
    <div>
      <PageHeader
        title="Security Reports"
        description="Permission changes and failed access attempts across the platform — InnovateFlow administrators only."
        helpKey="admin-audit"
      />

      {loadFailed && (
        <div role="alert" className="mb-4 flex items-center justify-between gap-3 p-3 bg-red-500/10 border border-red-200 rounded-lg text-sm text-red-700">
          <span className="flex items-center gap-2">
            <AlertCircle size={16} className="flex-shrink-0" />
            Couldn't load the security events. This is not an all-clear.
          </span>
          <button onClick={refetch} className="font-medium text-red-700 hover:text-red-800 underline">
            Retry
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Permission Changes */}
        <div className="light-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <Key size={18} className="text-iflow-500" />
            <h2 className="text-lg font-semibold text-gray-800">Permission Changes</h2>
          </div>
          {loadFailed ? unavailable : permissionChanges.length ? (
            <div className="space-y-3">
              {permissionChanges.map((entry) => (
                <div key={entry.event_id} className="flex items-start gap-3 py-2 border-b border-sky-200/50 last:border-0">
                  <ShieldCheck size={14} className="text-iflow-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-700">{humaniseAction(entry.action_type)}</p>
                    <p className="text-xs text-gray-500">User: {userLabel(entry.user_id, emailsById)}</p>
                    <p className="text-xs text-gray-500">{new Date(entry.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              compact
              icon={ShieldCheck}
              title="No recent permission changes"
              description="Grants, revokes and shares will appear here as they happen."
            />
          )}
        </div>

        {/* Failed Access Attempts */}
        <div className="light-card p-6">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={18} className="text-yellow-500" />
            <h2 className="text-lg font-semibold text-gray-800">Failed Access Attempts</h2>
          </div>
          {loadFailed ? unavailable : failedAccess.length ? (
            <div className="space-y-3">
              {failedAccess.map((entry) => (
                <div key={entry.event_id} className="flex items-start gap-3 py-2 border-b border-sky-200/50 last:border-0">
                  <AlertTriangle size={14} className="text-red-500 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-700">{humaniseAction(entry.action_type)}</p>
                    <p className="text-xs text-gray-500">User: {userLabel(entry.user_id, emailsById)} | Resource: {entry.resource_id}</p>
                    <p className="text-xs text-gray-500">{new Date(entry.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              compact
              icon={AlertTriangle}
              title="No failed access attempts"
              description="Denied, forbidden and blocked requests will appear here."
            />
          )}
        </div>
      </div>

      {/* All Security Events */}
      {data?.security_events?.length ? (
        <div className="mt-6 light-card p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">All Security Events ({data.count})</h2>
          <div className="space-y-2">
            {data.security_events.map((entry) => (
              <div key={entry.event_id} className="flex items-center gap-3 py-2 border-b border-sky-200/50 last:border-0">
                <span className="inline-flex px-2 py-0.5 text-xs font-medium bg-sky-500/10 text-gray-700 rounded-full">
                  {humaniseAction(entry.action_type)}
                </span>
                <span className="text-sm text-gray-700 flex-1">{userLabel(entry.user_id, emailsById)}</span>
                <span className="text-xs text-gray-500">{new Date(entry.timestamp).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}
